import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createCandidate, listCandidates, listJobs, type CandidateInput } from '../api/endpoints.js';
import { errorMessage } from '../api/client.js';
import { fmtDate } from '../lib/format.js';

const EMPTY_FORM: CandidateInput = { fullName: '', email: '', phone: '', jobId: '' };

export function CandidatesPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState<CandidateInput>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  const candidatesQuery = useQuery({
    queryKey: ['candidates', submittedSearch],
    queryFn: () => listCandidates(submittedSearch || undefined),
  });
  const jobsQuery = useQuery({ queryKey: ['jobs'], queryFn: listJobs });

  const addMutation = useMutation({
    mutationFn: (input: CandidateInput) =>
      createCandidate({
        fullName: input.fullName.trim(),
        email: input.email?.trim() || undefined,
        phone: input.phone?.trim() || undefined,
        jobId: input.jobId || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['candidates'] });
      void queryClient.invalidateQueries({ queryKey: ['applications'] });
      void queryClient.invalidateQueries({ queryKey: ['pipeline'] });
      setShowAdd(false);
      setForm(EMPTY_FORM);
    },
    onError: (err) => setFormError(errorMessage(err)),
  });

  function onSearch(e: FormEvent) {
    e.preventDefault();
    setSubmittedSearch(search.trim());
  }

  function onAdd(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    addMutation.mutate(form);
  }

  const candidates = candidatesQuery.data ?? [];
  const jobs = jobsQuery.data ?? [];

  return (
    <div className="page">
      <header className="page__header">
        <h1>Candidates</h1>
        <button type="button" className="btn btn--primary" onClick={() => setShowAdd(true)}>
          Add candidate
        </button>
      </header>

      <form onSubmit={onSearch} className="search-bar">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email, or phone…"
          aria-label="Search candidates"
        />
        <button type="submit" className="btn btn--secondary">
          Search
        </button>
      </form>

      {candidatesQuery.isLoading && <div className="spinner" aria-label="Loading candidates" />}
      {candidatesQuery.isError && (
        <div className="alert alert--error">Failed to load candidates.</div>
      )}

      {!candidatesQuery.isLoading && candidates.length === 0 && (
        <div className="empty-state">
          <h2>No candidates found</h2>
          <p>
            {submittedSearch
              ? 'Try a different search.'
              : 'Candidates appear here when they apply on Indeed, or add one manually.'}
          </p>
        </div>
      )}

      {candidates.length > 0 && (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Source</th>
                <th>Added</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map((c) => (
                <tr key={c.id}>
                  <td>{c.fullName}</td>
                  <td>{c.email ?? '—'}</td>
                  <td>{c.phone ?? '—'}</td>
                  <td>
                    <span className={`pill source-${c.source}`}>{c.source}</span>
                  </td>
                  <td>{fmtDate(c.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-label="Add candidate"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>Add candidate</h2>
            {formError && <div className="alert alert--error">{formError}</div>}
            <form onSubmit={onAdd} className="form-grid">
              <label className="field field--full">
                <span className="field__label">Full name</span>
                <input
                  value={form.fullName}
                  onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
                  required
                />
              </label>
              <label className="field">
                <span className="field__label">Email</span>
                <input
                  type="email"
                  value={form.email ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                />
              </label>
              <label className="field">
                <span className="field__label">Phone</span>
                <input
                  value={form.phone ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                />
              </label>
              <label className="field field--full">
                <span className="field__label">Apply to job (optional)</span>
                <select
                  value={form.jobId ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, jobId: e.target.value }))}
                >
                  <option value="">— no application —</option>
                  {jobs.map((j) => (
                    <option key={j.id} value={j.id}>
                      {j.title}
                    </option>
                  ))}
                </select>
              </label>
              <div className="modal__actions">
                <button type="button" className="btn btn--ghost" onClick={() => setShowAdd(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn--primary" disabled={addMutation.isPending}>
                  {addMutation.isPending ? 'Adding…' : 'Add candidate'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
